/** Persistent script-only data that is not part of the frozen StudioStore. */

const COMMENTS_KEY = "koko_studio_script_comments";
const SOUNDS_KEY = "koko_studio_script_line_sounds";
const FORMAT_KEY = "koko_studio_script_format";

export type CommentAuthor = "you" | "koko";

export interface CommentReply {
  id: string;
  text: string;
  author: CommentAuthor;
  createdAt: number;
}

/** One anchored thread. The root message and its replies deliberately share a tiny model. */
export interface ScriptComment {
  id: string;
  sceneId: string;
  elementId: string;
  quote: string;
  start: number;
  end: number;
  text: string;
  author: CommentAuthor;
  replies: CommentReply[];
  resolved: boolean;
  detached: boolean;
  createdAt: number;
}

export type Delivery = "neutral" | "whisper" | "shout" | "warm";

export interface LineSound {
  delivery: Delivery;
  tone?: string;
  speed?: number;
}

export interface ScriptFormatState {
  dualDialogueElementIds: string[];
  pageBreakBeforeElementIds: string[];
}

function genId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined" || !window.localStorage) return fallback;
  const raw = window.localStorage.getItem(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A script remains editable if browser storage is unavailable.
  }
}

function migrateComment(value: Partial<ScriptComment>): ScriptComment | null {
  if (!value || typeof value.id !== "string" || typeof value.elementId !== "string") {
    return null;
  }
  const quote = typeof value.quote === "string" ? value.quote : "";
  const start = Number.isFinite(value.start) ? Math.max(0, Number(value.start)) : 0;
  const fallbackEnd = start + quote.length;
  return {
    id: value.id,
    sceneId: typeof value.sceneId === "string" ? value.sceneId : "",
    elementId: value.elementId,
    quote,
    start,
    end: Number.isFinite(value.end) ? Math.max(start, Number(value.end)) : fallbackEnd,
    text: typeof value.text === "string" ? value.text : "",
    author: value.author === "koko" ? "koko" : "you",
    replies: Array.isArray(value.replies) ? value.replies : [],
    resolved: !!value.resolved,
    detached: !!value.detached,
    createdAt: Number.isFinite(value.createdAt) ? Number(value.createdAt) : Date.now(),
  };
}

export class ScriptCommentStore {
  private comments: ScriptComment[];
  private readonly listeners = new Set<() => void>();

  constructor() {
    this.comments = readJson<Partial<ScriptComment>[]>(COMMENTS_KEY, [])
      .map(migrateComment)
      .filter((comment): comment is ScriptComment => !!comment);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  list(): readonly ScriptComment[] {
    return this.comments;
  }

  get(id: string): ScriptComment | null {
    return this.comments.find((comment) => comment.id === id) ?? null;
  }

  listForElement(elementId: string): ScriptComment[] {
    return this.comments.filter((comment) => comment.elementId === elementId);
  }

  add(input: {
    sceneId: string;
    elementId: string;
    quote: string;
    start?: number;
    end?: number;
    text: string;
    author?: CommentAuthor;
  }): ScriptComment {
    const start = Math.max(0, input.start ?? 0);
    const end = Math.max(start, input.end ?? start + input.quote.length);
    const comment: ScriptComment = {
      id: genId("cm"),
      sceneId: input.sceneId,
      elementId: input.elementId,
      quote: input.quote,
      start,
      end,
      text: input.text.trim(),
      author: input.author ?? "you",
      replies: [],
      resolved: false,
      detached: false,
      createdAt: Date.now(),
    };
    this.comments = [...this.comments, comment];
    this.flush();
    return comment;
  }

  reply(id: string, text: string, author: CommentAuthor = "you"): boolean {
    const trimmed = text.trim();
    if (!trimmed) return false;
    return this.patch(id, (comment) => ({
      ...comment,
      replies: [
        ...comment.replies,
        { id: genId("reply"), text: trimmed, author, createdAt: Date.now() },
      ],
    }));
  }

  setResolved(id: string, resolved: boolean): boolean {
    return this.patch(id, (comment) => ({ ...comment, resolved }));
  }

  /** Keep a range attached through ordinary insertions/deletions when its quote survives. */
  rebaseElement(elementId: string, before: string, after: string): void {
    if (before === after) return;
    let changed = false;
    this.comments = this.comments.map((comment) => {
      if (comment.elementId !== elementId || comment.detached) return comment;
      const exact = nearestIndexOf(after, comment.quote, comment.start);
      if (exact >= 0) {
        changed = changed || exact !== comment.start;
        return { ...comment, start: exact, end: exact + comment.quote.length };
      }

      const prefix = commonPrefixLength(before, after);
      const suffix = commonSuffixLength(before.slice(prefix), after.slice(prefix));
      const oldEditEnd = before.length - suffix;
      const newEditEnd = after.length - suffix;
      if (oldEditEnd <= comment.start) {
        const delta = newEditEnd - oldEditEnd;
        changed = true;
        return { ...comment, start: comment.start + delta, end: comment.end + delta };
      }
      changed = true;
      return { ...comment, detached: true };
    });
    if (changed) this.flush();
  }

  detachElement(elementId: string): void {
    let changed = false;
    this.comments = this.comments.map((comment) => {
      if (comment.elementId !== elementId || comment.detached) return comment;
      changed = true;
      return { ...comment, detached: true };
    });
    if (changed) this.flush();
  }

  private patch(id: string, apply: (comment: ScriptComment) => ScriptComment): boolean {
    const index = this.comments.findIndex((comment) => comment.id === id);
    if (index < 0) return false;
    const next = [...this.comments];
    next[index] = apply(next[index]);
    this.comments = next;
    this.flush();
    return true;
  }

  private flush(): void {
    writeJson(COMMENTS_KEY, this.comments);
    this.listeners.forEach((listener) => listener());
  }
}

export class ScriptFormatStore {
  private state: ScriptFormatState;
  private readonly listeners = new Set<() => void>();

  constructor() {
    const value = readJson<Partial<ScriptFormatState>>(FORMAT_KEY, {});
    this.state = {
      dualDialogueElementIds: value.dualDialogueElementIds ?? [],
      pageBreakBeforeElementIds: value.pageBreakBeforeElementIds ?? [],
    };
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  isDual(elementId: string): boolean {
    return this.state.dualDialogueElementIds.includes(elementId);
  }

  toggleDual(elementId: string): void {
    this.state.dualDialogueElementIds = toggleInList(
      this.state.dualDialogueElementIds,
      elementId,
    );
    this.flush();
  }

  hasPageBreak(elementId: string): boolean {
    return this.state.pageBreakBeforeElementIds.includes(elementId);
  }

  togglePageBreak(elementId: string): void {
    this.state.pageBreakBeforeElementIds = toggleInList(
      this.state.pageBreakBeforeElementIds,
      elementId,
    );
    this.flush();
  }

  private flush(): void {
    writeJson(FORMAT_KEY, this.state);
    this.listeners.forEach((listener) => listener());
  }
}

export class LineSoundStore {
  private byElement: Record<string, LineSound>;
  private readonly listeners = new Set<() => void>();

  constructor() {
    this.byElement = readJson<Record<string, LineSound>>(SOUNDS_KEY, {});
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get(elementId: string): LineSound | null {
    return this.byElement[elementId] ?? null;
  }

  set(elementId: string, sound: LineSound | null): void {
    const next = { ...this.byElement };
    if (sound === null) delete next[elementId];
    else next[elementId] = sound;
    this.byElement = next;
    writeJson(SOUNDS_KEY, this.byElement);
    this.listeners.forEach((listener) => listener());
  }
}

function toggleInList(values: string[], id: string): string[] {
  return values.includes(id) ? values.filter((value) => value !== id) : [...values, id];
}

function commonPrefixLength(a: string, b: string): number {
  let index = 0;
  while (index < a.length && index < b.length && a[index] === b[index]) index += 1;
  return index;
}

function commonSuffixLength(a: string, b: string): number {
  let index = 0;
  while (
    index < a.length &&
    index < b.length &&
    a[a.length - index - 1] === b[b.length - index - 1]
  ) {
    index += 1;
  }
  return index;
}

function nearestIndexOf(text: string, quote: string, near: number): number {
  if (!quote) return -1;
  const indexes: number[] = [];
  let index = text.indexOf(quote);
  while (index >= 0) {
    indexes.push(index);
    index = text.indexOf(quote, index + 1);
  }
  if (indexes.length === 0) return -1;
  return indexes.reduce((best, value) =>
    Math.abs(value - near) < Math.abs(best - near) ? value : best,
  );
}

export const DEFAULT_LINE_SOUND: LineSound = { delivery: "neutral", speed: 1 };
